import { PlayerName } from "@/components/PlayerCardDialog";
import React from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { fmt } from "@/lib/format";
import { Loader2, ShieldQuestion, Timer, Layers } from "lucide-react";

const POS = ["C", "LW", "RW", "D"];

const MiniTitle = ({ icon: Icon, children }) => (
  <p className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-cyan-600">
    {Icon && <Icon className="h-3 w-3" />} {children}
  </p>
);

const DefenseProfile = ({ hist }) => {
  const l5 = hist.aggregates.last5;
  const l10 = hist.aggregates.last10;
  const l10max = Math.max(...POS.map((p) => l10.goals_allowed[p]));
  const firstPrimary = l10.first_goal_primary;
  // Season GA/GP by position derived from the SAME game-log source as L5/L10
  // (avoids contradicting a sparse/stale external per-position season stat).
  const log = hist.game_log || [];
  const gp = log.length;
  const seasonGa = {};
  POS.forEach((p) => {
    const tot = log.reduce((s, r) => s + ((r.goals_allowed && r.goals_allowed[p]) || 0), 0);
    seasonGa[p] = gp ? tot / gp : null;
  });
  return (
    <div>
      <MiniTitle icon={ShieldQuestion}>Positional Weakness · Combined</MiniTitle>
      <div className="overflow-hidden rounded-lg border border-slate-800">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-900/80 font-mono text-[9px] uppercase tracking-wider text-slate-500">
              <th className="px-2 py-1.5 text-left">Pos</th>
              <th className="px-2 py-1.5 text-right text-cyan-500" title={`Season goals allowed per game by position (last ${gp} games)`}>GA/GP</th>
              <th className="px-2 py-1.5 text-right" title="Last 5 goals allowed">L5 GA</th>
              <th className="px-2 py-1.5 text-right" title="Last 10 goals allowed">L10 GA</th>
              <th className="px-2 py-1.5 text-right" title="First goal allowed (last 10)">1stG</th>
              <th className="px-2 py-1.5 text-right" title="2+ goal games (last 10)">Multi</th>
            </tr>
          </thead>
          <tbody>
            {POS.map((p) => {
              const isMax = l10.goals_allowed[p] === l10max && l10max > 0;
              return (
                <tr key={p} className={`border-t border-slate-800/60 ${isMax ? "bg-rose-950/25" : ""}`}>
                  <td className="px-2 py-1.5 font-mono text-[11px] font-bold text-slate-300">{p}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-200 tabular-nums">{seasonGa[p] === null ? "—" : fmt(seasonGa[p])}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-300 tabular-nums">{l5.goals_allowed[p]}</td>
                  <td className={`px-2 py-1.5 text-right font-mono text-[11px] tabular-nums ${isMax ? "font-bold text-rose-300" : "text-slate-300"}`}>{l10.goals_allowed[p]}</td>
                  <td className={`px-2 py-1.5 text-right font-mono text-[11px] tabular-nums ${firstPrimary === p ? "font-bold text-amber-300" : "text-slate-400"}`}>{l10.first_goal[p]}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-400 tabular-nums">{l10.multi_goal_games[p]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-2 font-mono text-[9px] uppercase tracking-wider text-slate-500">
        <span className="flex items-center gap-1"><Timer className="h-3 w-3 text-amber-500" /> First-goal primary: <b className="text-amber-300">{firstPrimary || "—"}</b></span>
      </div>
    </div>
  );
};

const PosCells = ({ obj, highlight }) => (
  <>
    {POS.map((p) => (
      <td key={p} className={`px-2 py-1 text-right font-mono text-[10px] tabular-nums ${highlight === p ? "font-bold text-rose-300" : "text-slate-300"}`}>{obj[p]}</td>
    ))}
  </>
);

const GoalieSplits = ({ goalies, origin }) => {
  if (!goalies?.length) return null;
  return (
    <div>
      <MiniTitle icon={ShieldQuestion}>Goalie Position Splits</MiniTitle>
      <div className="space-y-2">
        {goalies.map((g) => (
          <div key={g.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-display text-[12px] font-bold uppercase text-slate-100">{g.name}</span>
              <span className="font-mono text-[9px] uppercase text-slate-500">{g.games} GP</span>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="font-mono text-[8px] uppercase tracking-wider text-slate-600">
                  <th className="px-2 py-0.5 text-left">Origin</th>
                  {POS.map((p) => <th key={p} className="px-2 py-0.5 text-right">{p}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr><td className="px-2 py-0.5 font-mono text-[9px] uppercase text-slate-500" title="Total goals allowed">GA</td><PosCells obj={g.goals_allowed} /></tr>
                <tr><td className="px-2 py-0.5 font-mono text-[9px] uppercase text-slate-500" title="Games with a goal from that position">Gm</td><PosCells obj={g.games_by_pos || {}} /></tr>
                <tr><td className="px-2 py-0.5 font-mono text-[9px] uppercase text-slate-500" title="First goal allowed">1st</td><PosCells obj={g.first_goal} /></tr>
                <tr><td className="px-2 py-0.5 font-mono text-[9px] uppercase text-slate-500" title="Multi-goal games">Mlt</td><PosCells obj={g.multi_goal_games} /></tr>
                {g.l5 && <tr><td className="px-2 py-0.5 font-mono text-[9px] uppercase text-slate-500">L5</td><PosCells obj={g.l5} /></tr>}
                {g.l10 && <tr><td className="px-2 py-0.5 font-mono text-[9px] uppercase text-slate-500">L10</td><PosCells obj={g.l10} /></tr>}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      <GoalOriginMatch origin={origin} />
    </div>
  );
};

const GoalOriginMatch = ({ origin }) => {
  if (!origin || !origin.available) return null;
  const lf = origin.line_freq || {};
  const lf10 = origin.line_freq_l10 || {};
  const events = origin.goal_events || [];
  const slotsByPos = { C: [], LW: [], RW: [], D: [] };
  Object.keys(lf).forEach((slot) => {
    const pos = slot === "UNK" ? null : slot.replace(/[0-9]+$/, "");
    if (pos && slotsByPos[pos]) slotsByPos[pos].push(slot);
  });
  Object.values(slotsByPos).forEach((a) => a.sort());

  return (
    <div className="mt-2 space-y-2" data-testid="goal-origin-match">
      {/* goal-by-goal */}
      <div className="rounded-lg border border-cyan-900/40 bg-slate-900/40 p-2">
        <MiniTitle icon={Layers}>Goal Origin vs {origin.goalie} · Goal-by-Goal</MiniTitle>
        {!origin.line_data_available && (
          <p className="mb-1 font-mono text-[8px] uppercase tracking-wider text-amber-600/80">Some historical lines unavailable · shown as UNK</p>
        )}
        {events.length ? (
          <div className="max-h-52 overflow-y-auto rounded border border-slate-800">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-slate-900">
                <tr className="font-mono text-[8px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-1 text-left">Date</th>
                  <th className="px-2 py-1 text-left">Scorer</th>
                  <th className="px-2 py-1 text-center">Pos</th>
                  <th className="px-2 py-1 text-center">Line</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i} className="border-t border-slate-800/60">
                    <td className="px-2 py-1 font-mono text-[10px] text-slate-400 whitespace-nowrap">{(e.date || "").slice(5)}</td>
                    <td className="px-2 py-1">
                      <span className="font-mono text-[10px] font-bold text-cyan-400">{e.jersey != null ? `#${e.jersey}` : ""}</span>{" "}
                      <PlayerName name={e.scorer} team={e.team} className="font-display text-[10px] font-semibold uppercase text-slate-200" />
                    </td>
                    <td className="px-2 py-1 text-center font-mono text-[10px] font-bold text-slate-300">{e.position}</td>
                    <td className={`px-2 py-1 text-center font-mono text-[10px] font-bold ${e.slot ? "text-cyan-300" : "text-slate-600"}`}>{e.slot || "UNK"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="py-2 text-center font-mono text-[10px] text-slate-600">Insufficient history</p>}
      </div>

      {/* line frequency */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2">
        <MiniTitle icon={ShieldQuestion}>Line Frequency · Goals → Games</MiniTitle>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {POS.map((p) => (
            <div key={p} className="rounded border border-slate-800 bg-slate-900/50 p-1.5">
              <p className="mb-1 font-mono text-[10px] font-bold uppercase text-slate-400">{p}</p>
              {slotsByPos[p].length ? slotsByPos[p].map((slot) => (
                <div key={slot} className="flex items-center justify-between gap-1 py-0.5">
                  <span className="font-mono text-[10px] font-bold text-cyan-300">{slot}</span>
                  <span className="font-mono text-[9px] text-slate-400 tabular-nums">
                    {lf[slot].goals}G / {lf[slot].games}Gm <span className="text-slate-600">· L10 {lf10[slot] || 0}</span>
                  </span>
                </div>
              )) : <p className="font-mono text-[9px] text-slate-600">—</p>}
            </div>
          ))}
        </div>
      </div>

      {/* current lineup match */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2">
        <MiniTitle icon={Layers}>Goal Origin → Current {origin.opponent} Lineup</MiniTitle>
        <p className="mb-1.5 font-mono text-[8px] uppercase tracking-wider text-slate-600">Goalie {origin.goalie} · matched to today's lines</p>
        <div className="space-y-1">
          {origin.current_opponent.length ? origin.current_opponent.map((c, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-md border border-slate-800/70 bg-slate-900/40 px-2 py-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="font-mono text-[11px] font-bold text-cyan-400">{c.jersey != null ? `#${c.jersey}` : "#—"}</span>
                <PlayerName name={c.name} team={origin.opponent} className="font-display text-[11px] font-semibold uppercase text-slate-200" />
                <span className="rounded bg-slate-800 px-1 font-mono text-[9px] text-slate-300">{c.position}</span>
                <span className="font-mono text-[9px] text-slate-500">{c.line}</span>
              </span>
              <span className="font-mono text-[10px] text-amber-300 tabular-nums">
                {c.goalie_ga} GA <span className="text-slate-500">· L10 {c.goalie_l10} · {c.goalie_games} Gm</span>
              </span>
            </div>
          )) : <p className="py-2 text-center font-mono text-[10px] text-slate-600">Lineup unavailable</p>}
        </div>
      </div>
    </div>
  );
};

const GameLog = ({ rows }) => (
  <div>
    <MiniTitle icon={Layers}>Game-by-Game · Last {rows.length}</MiniTitle>
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-900/80 font-mono text-[9px] uppercase tracking-wider text-slate-500">
            <th className="px-2 py-1.5 text-left">Date</th>
            <th className="px-2 py-1.5 text-left">Opp</th>
            <th className="px-2 py-1.5 text-center">Final</th>
            <th className="px-2 py-1.5 text-center">1stG</th>
            {POS.map((p) => <th key={p} className="px-2 py-1.5 text-right">{p}</th>)}
            <th className="px-2 py-1.5 text-center">Multi</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-800/60">
              <td className="px-2 py-1 font-mono text-[10px] text-slate-400 whitespace-nowrap">{(r.date || "").slice(5)}</td>
              <td className="px-2 py-1 font-mono text-[10px] text-slate-300">{r.home_away === "A" ? "@" : ""}{r.opponent}</td>
              <td className={`px-2 py-1 text-center font-mono text-[10px] font-semibold ${r.result === "W" ? "text-emerald-400" : "text-rose-400"}`}>{r.final}</td>
              <td className="px-2 py-1 text-center font-mono text-[10px] font-bold text-amber-300">{r.first_goal_position || "—"}</td>
              {POS.map((p) => <td key={p} className="px-2 py-1 text-right font-mono text-[10px] text-slate-300 tabular-nums">{r.goals_allowed[p]}</td>)}
              <td className="px-2 py-1 text-center font-mono text-[10px] font-semibold text-cyan-300">{r.multi_goal_positions.join(",") || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const TeamHistoryColumn = ({ label, hist, spStats, origin }) => {
  if (!hist || !hist.ok) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-display text-sm font-bold uppercase text-white">{label}</span>
          <StatusBadge status="UNAVAILABLE" size="xs" />
        </div>
        <p className="py-6 text-center font-mono text-[11px] text-slate-600">Insufficient history</p>
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <div className="flex items-center justify-between">
        <span className="font-display text-sm font-bold uppercase text-white">{label} defense</span>
        <StatusBadge status="UPDATED" size="xs" />
      </div>
      <DefenseProfile hist={hist} />
      <GoalieSplits goalies={hist.goalies} origin={origin} />
      <GameLog rows={hist.game_log} />
    </div>
  );
};

export const HistoryTab = ({ loading, error, history, game, goalieOrigin, onRetry }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
        <p className="mt-3 font-mono text-[11px] uppercase tracking-widest">Building historical goal-position engine…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <p className="font-mono text-[11px] uppercase tracking-widest text-rose-400">Insufficient history</p>
        <button onClick={onRetry} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
      </div>
    );
  }
  if (!history) return null;
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid={`history-panels-${game.away.abbrev}-${game.home.abbrev}`}>
      <TeamHistoryColumn label={game.away.abbrev} hist={history.away} spStats={game.away_stats} origin={goalieOrigin?.away} />
      <TeamHistoryColumn label={game.home.abbrev} hist={history.home} spStats={game.home_stats} origin={goalieOrigin?.home} />
    </div>
  );
};

export default HistoryTab;
